import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  dateFnsLocaleCode,
  defaultLocale,
  intlLocale,
  localeLabels,
  Locale,
  supportedLocales,
  TranslationKey,
} from "@/i18n/messages";
import {
  setCurrentLocale,
  translate,
  translatePlural,
  type PluralKey,
  type TranslationValues,
} from "@/i18n/translate";

type LocalizationContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  localeLabel: string;
  supportedLocales: readonly Locale[];
  /** the text of a key in the current language, `{name}` placeholders filled from `values` */
  t: (key: TranslationKey, values?: TranslationValues) => string;
  /** a count-dependent text: tp("count.pizzas", 3) -> "3 pizzy" */
  tp: (key: PluralKey, count: number, values?: TranslationValues) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** BCP 47 tag for Intl / toLocaleString */
  intlLocale: string;
  dateFnsLocaleCode: "cs" | "en-US";
};

const STORAGE_KEY = "pizza-admin:locale:v1";

const resolveLocale = (value?: string | null): Locale | null => {
  if (!value) {
    return null;
  }

  const normalized = value.toLowerCase();
  return supportedLocales.find((locale) => normalized.startsWith(locale)) ?? null;
};

/** English (or the provider's fallback) unless the user picked a language before. */
const getInitialLocale = (fallback: Locale, storageKey: string): Locale => {
  if (typeof window === "undefined") {
    return fallback;
  }
  try {
    return resolveLocale(window.localStorage.getItem(storageKey)) ?? fallback;
  } catch {
    return fallback;
  }
};

const LocalizationContext =
  createContext<LocalizationContextValue | null>(null);

export function LocalizationProvider({
  children,
  fallbackLocale = defaultLocale,
  storageKey = STORAGE_KEY,
}: {
  children: React.ReactNode;
  /** the language before the user picks one (the station tablets: Czech) */
  fallbackLocale?: Locale;
  /** where the choice is kept (the station tablets keep their own, apart from the admin's) */
  storageKey?: string;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = getInitialLocale(fallbackLocale, storageKey);
    setCurrentLocale(initial);
    return initial;
  });
  // helpers outside React (translate(), date formats) follow the choice from this render on
  setCurrentLocale(locale);

  const setLocale = useCallback((next: Locale) => {
    setCurrentLocale(next);
    setLocaleState(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(storageKey, locale);
    } catch {
      // private mode: the choice lasts for this visit
    }
  }, [locale, storageKey]);

  const value = useMemo<LocalizationContextValue>(
    () => ({
      locale,
      setLocale,
      localeLabel: localeLabels[locale],
      supportedLocales,
      t: (key, values) => translate(key, values, locale),
      tp: (key, count, values) => translatePlural(key, count, values, locale),
      formatNumber: (number, options) =>
        new Intl.NumberFormat(intlLocale[locale], options).format(number),
      intlLocale: intlLocale[locale],
      dateFnsLocaleCode: dateFnsLocaleCode[locale],
    }),
    [locale, setLocale],
  );

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization() {
  const context = useContext(LocalizationContext);

  if (!context) {
    throw new Error("useLocalization must be used within LocalizationProvider");
  }

  return context;
}
